/** Conversational ticket status steps for the terminal assistant. */
import { todayISO, ticketQuotedTotal, fmtAmt, leadOutstandingBalance } from './utils.js';
import { STATUS_LABELS } from './status-pipelines.js';
import { capitalizeName, upperDoc } from './assistant-ticket-new.js';
import { BOOKING_SYSTEMS, calcTicketQuoteTotals, isTicketQuickIntake } from './assistant-ticket-v2.js';
import {
  parseSmartDate,
  parseSmartDateOnly,
  parseSmartDateTime,
  validateSmartDate,
  validateSmartDateTime,
  DATE_HINT,
  DATETIME_HINT,
  datetimeToDate,
  isSkip,
} from './assistant-datetime.js';

function advancePaidFromLead(lead = {}) {
  const extra = lead.extra || {};
  return extra.advance_paid ?? extra.stage_data?.temporary_booking?.advance ?? 0;
}

function tempBalanceFromLead(lead = {}) {
  const extra = lead.extra || {};
  const v = extra.balance_due ?? extra.stage_data?.temporary_booking?.balance_due;
  return v != null && v !== '' ? v : null;
}

function sd(lead, status) {
  return lead?.extra?.stage_data?.[status] || {};
}

function pick(...vals) {
  return vals.find((v) => v != null && v !== '');
}

function datetimeLocalToDate(val) {
  return datetimeToDate(val);
}

function parseYesNo(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (['yes', 'y', 'true', '1', 'done'].includes(s)) return true;
  if (['no', 'n', 'false', '0', 'skip', '-', 'none', 'n/a'].includes(s)) return false;
  return null;
}

export function parseAssistantDatetime(raw) {
  return parseSmartDateTime(raw);
}

export function parseAssistantDate(raw) {
  return parseSmartDateOnly(raw);
}

export function leadHasDueBalance(lead) {
  return leadOutstandingBalance(lead) > 0;
}

export function statusDisplayName(status) {
  return STATUS_LABELS[status] || status;
}

function ticketPax(extra = {}, lead = {}) {
  const p = extra.pax || {};
  const adt = Number(p.adt) || 0;
  const chd = Number(p.chd) || 0;
  const inf = Number(p.inf) || 0;
  if (isTicketQuickIntake(lead) && !adt && !chd && !inf) {
    return { adt: 1, chd: 0, inf: 0 };
  }
  return { adt, chd, inf };
}

function resolveBookingSystemValue(system, other) {
  if (system === 'Other') return String(other || '').trim() || 'Other';
  return system;
}

function parseBookingSystem(raw) {
  const s = String(raw || '').trim();
  const n = Number(s);
  if (Number.isInteger(n) && n >= 1 && n <= BOOKING_SYSTEMS.length) return BOOKING_SYSTEMS[n - 1];
  const hit = BOOKING_SYSTEMS.find((x) => x.toLowerCase() === s.toLowerCase());
  return hit || (s || null);
}

function lkrStep(key, label, optional = false) {
  return {
    key,
    ask: `${label} (LKR)?`,
    optional,
    validate: (v) => {
      if (optional && isSkip(v)) return null;
      const n = Number(String(v).trim().replace(/,/g, ''));
      if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
      return null;
    },
    parse: (v) => (optional && isSkip(v) ? null : Number(String(v).trim().replace(/,/g, ''))),
  };
}

export function ticketStageSteps(status, lead = {}) {
  const extra = lead.extra || {};
  const stage = sd(lead, status);

  switch (status) {
    case 'new':
      return [];

    case 'quotation_sent': {
      if (isTicketQuickIntake(lead)) {
        return [
          {
            key: 'quoted_at',
            ask: 'Quotation sent on (date + time)?',
            hint: DATETIME_HINT,
            validate: (v) => validateSmartDateTime(v),
            parse: (v) => parseAssistantDatetime(v),
            default: () => pick(stage.quoted_at, extra.quoted_at),
          },
          {
            key: 'quote_description',
            ask: 'Quotation details / notes?',
            hint: 'Route, airline, fare, conditions — skip if none',
            optional: true,
            parse: (v) => (isSkip(v) ? null : String(v).trim()),
            default: () => pick(stage.quote_description, stage.note, extra.quotation_note),
          },
        ];
      }
      const pax = ticketPax(extra, lead);
      const steps = [
        {
          key: 'quoted_at',
          ask: 'Quotation sent on (date + time)?',
          hint: DATETIME_HINT,
          validate: (v) => validateSmartDateTime(v),
          parse: (v) => parseAssistantDatetime(v),
          default: () => pick(stage.quoted_at, extra.quoted_at),
        },
        {
          key: 'airline',
          ask: 'Airline quoted?',
          validate: (v) => (!String(v).trim() ? 'Enter airline.' : null),
          parse: (v) => String(v).trim(),
          default: () => pick(lead.airline, stage.airline),
        },
        {
          key: 'quote_description',
          ask: 'Brief description of quote sent?',
          hint: 'WhatsApp/email summary · skip if none',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
          default: () => pick(stage.quote_description, stage.note, extra.quotation_note),
        },
      ];
      if (pax.adt > 0) {
        steps.push(
          lkrStep('cost_adt', `Net cost per adult (×${pax.adt})`),
          lkrStep('profit_adt', `Profit per adult (×${pax.adt})`),
        );
      }
      if (pax.chd > 0) {
        steps.push(
          lkrStep('cost_chd', `Net cost per child (×${pax.chd})`, true),
          lkrStep('profit_chd', `Profit per child (×${pax.chd})`, true),
        );
      }
      if (pax.inf > 0) {
        steps.push(
          lkrStep('cost_inf', `Net cost per infant (×${pax.inf})`, true),
          lkrStep('profit_inf', `Profit per infant (×${pax.inf})`, true),
        );
      }
      steps.push({
        key: 'quote_valid_till',
        ask: 'Quote valid till?',
        hint: `${DATE_HINT} · skip`,
        optional: true,
        parse: (v) => (isSkip(v) ? null : parseAssistantDate(v)),
        validate: (v) => (isSkip(v) ? null : validateSmartDate(v, { required: false })),
        default: () => pick(stage.quote_valid_till, extra.quote_valid_till),
      });
      return steps;
    }

    case 'temporary_booking': {
      const quoted = ticketQuotedTotal(lead) || stage.quoted_total || '';
      const systems = BOOKING_SYSTEMS.map((s, i) => `${i + 1}. ${s}`).join('  ');
      const steps = [];
      if (isTicketQuickIntake(lead)) {
        const pax = ticketPax(extra, lead);
        if (pax.adt > 0) {
          steps.push(
            lkrStep('cost_adt', `Net cost per adult (×${pax.adt})`),
            lkrStep('profit_adt', `Profit per adult (×${pax.adt})`),
          );
        }
        if (pax.chd > 0) {
          steps.push(
            lkrStep('cost_chd', `Net cost per child (×${pax.chd})`, true),
            lkrStep('profit_chd', `Profit per child (×${pax.chd})`, true),
          );
        }
        if (pax.inf > 0) {
          steps.push(
            lkrStep('cost_inf', `Net cost per infant (×${pax.inf})`, true),
            lkrStep('profit_inf', `Profit per infant (×${pax.inf})`, true),
          );
        }
      }
      steps.push(
        {
          key: 'pnr',
          ask: 'PNR / TKT number?',
          validate: (v) => (!String(v).trim() ? 'PNR is required.' : null),
          parse: (v) => upperDoc(v),
          default: () => pick(lead.pnr, stage.pnr),
        },
        {
          key: 'booking_system',
          ask: () => `Where was the booking made?\n${systems}`,
          validate: (v) => (!parseBookingSystem(v) ? 'Pick 1–4 or type system name.' : null),
          parse: (v) => parseBookingSystem(v),
          default: () => pick(extra.booking_system, stage.booking_system),
        },
        {
          key: 'booking_system_other',
          ask: 'Enter where the booking was made:',
          when: (answers) => answers.booking_system === 'Other',
          validate: (v) => (!String(v).trim() ? 'Enter where the booking was made.' : null),
          parse: (v) => String(v).trim(),
          default: () => pick(extra.booking_system_other, stage.booking_system_other),
        },
        {
          key: 'booking_expires',
          ask: 'Booking expires on (date + time)?',
          hint: DATETIME_HINT,
          validate: (v) => validateSmartDateTime(v),
          parse: (v) => parseAssistantDatetime(v),
          default: () => pick(extra.booking_expires, stage.booking_expires),
        },
        {
          key: 'advance',
          ask: () => {
            const q = quoted ? `Advance received (LKR)? Quoted: ${fmtAmt(quoted)}` : 'Advance received (LKR)?';
            return q;
          },
          optional: true,
          validate: (v) => {
            if (isSkip(v)) return null;
            const n = Number(String(v).trim());
            if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR (0 or more).';
            return null;
          },
          parse: (v) => (isSkip(v) ? 0 : Number(String(v).trim())),
          default: () => pick(stage.advance, extra.advance_paid, ''),
        },
      );
      return steps;
    }

    case 'booking_confirmed': {
      const quoted = ticketQuotedTotal(lead) || stage.quoted_total || 0;
      const advance = advancePaidFromLead(lead);
      const tempBal = tempBalanceFromLead(lead);
      const defaultPay = tempBal != null ? tempBal : Math.max(0, Math.round((quoted || 0) - (advance || 0)));
      return [
        {
          key: '_intro',
          ask: () => `Payment stage. Advance paid: LKR ${fmtAmt(advance)} · Balance due: LKR ${fmtAmt(defaultPay)}`,
          hint: 'Press enter to continue.',
          optional: true,
          parse: () => null,
          validate: () => null,
        },
        {
          key: 'paid_on',
          ask: 'Payment received on?',
          hint: DATE_HINT,
          validate: validateSmartDate,
          parse: (v) => parseAssistantDate(v) || todayISO(),
          default: () => pick(extra.paid_on, stage.paid_on, todayISO()),
        },
        {
          key: 'payment_amount',
          ask: () => `Amount received now (LKR)? Suggested: ${fmtAmt(defaultPay)}`,
          validate: (v) => {
            const n = Number(String(v).trim());
            if (!Number.isFinite(n) || n <= 0) return 'Enter payment amount in LKR.';
            return null;
          },
          parse: (v) => Number(String(v).trim()),
          default: () => defaultPay,
        },
        {
          key: 'remain_pay_by',
          ask: 'Remaining balance due by?',
          hint: DATETIME_HINT,
          when: (answers) => {
            const payment = answers.payment_amount || 0;
            const totalPaid = (advance || 0) + payment;
            return (quoted || 0) > 0 && totalPaid < (quoted || 0);
          },
          validate: validateSmartDateTime,
          parse: (v) => parseAssistantDatetime(v),
          default: () => pick(extra.remain_pay_by, stage.remain_pay_by),
        },
        {
          key: 'note',
          ask: 'Payment notes?',
          hint: 'Type skip if none.',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
        },
      ];
    }

    case 'ticket_issued': {
      const quoted = ticketQuotedTotal(lead) || 0;
      const advance = advancePaidFromLead(lead);
      const defaultPay = Math.max(0, Math.round((quoted || 0) - (advance || 0)));
      return [
        {
          key: 'issued_date',
          ask: 'Ticket issued date?',
          hint: DATE_HINT,
          validate: validateSmartDate,
          parse: (v) => parseAssistantDate(v) || todayISO(),
          default: () => pick(extra.ticket_issued_date, stage.issued_date, todayISO()),
        },
        {
          key: 'payment_amount',
          ask: () => `Payment received (LKR)? Suggested balance: ${fmtAmt(defaultPay)}`,
          optional: true,
          validate: (v) => {
            if (isSkip(v)) return null;
            const n = Number(String(v).trim());
            if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
            return null;
          },
          parse: (v) => (isSkip(v) ? defaultPay : Number(String(v).trim())),
          default: () => pick(stage.payment_amount, defaultPay, ''),
        },
        {
          key: 'pnr',
          ask: 'Confirm PNR / ticket number?',
          validate: (v) => (!String(v).trim() ? 'PNR is required.' : null),
          parse: (v) => upperDoc(v),
          default: () => pick(lead.pnr, stage.pnr),
        },
        {
          key: 'note',
          ask: 'Notes (e.g. PDF sent)?',
          hint: 'Type skip if none.',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
        },
      ];
    }

    default:
      return [];
  }
}

export function activeStageSteps(status, lead, answers = {}) {
  return ticketStageSteps(status, lead).filter((step) => !step.when || step.when(answers, lead));
}

export function buildStatusPatchBody(type, status, lead, answers = {}) {
  const d = { ...answers };
  delete d._intro;

  const body = { status, stage_data: {} };

  if (status === 'quotation_sent') {
    if (isTicketQuickIntake(lead)) {
      body.stage_data = {
        quoted_at: d.quoted_at || null,
        quote_description: d.quote_description || null,
      };
      body.note = d.quote_description || null;
      body.extra_patch = {
        quotation_note: d.quote_description || null,
        quoted_at: d.quoted_at || null,
      };
      return body;
    }
    const pax = ticketPax(lead.extra || {}, lead);
    const breakdown = calcTicketQuoteTotals(pax, d);
    body.stage_data = {
      quoted_at: d.quoted_at || null,
      airline: d.airline || null,
      quote_description: d.quote_description || null,
      quote_valid_till: d.quote_valid_till || null,
      cost_adt: d.cost_adt ?? null,
      profit_adt: d.profit_adt ?? null,
      cost_chd: d.cost_chd ?? null,
      profit_chd: d.profit_chd ?? null,
      cost_inf: d.cost_inf ?? null,
      profit_inf: d.profit_inf ?? null,
      cost_total: breakdown.cost,
      profit_total: breakdown.profit,
      total: breakdown.total,
    };
    body.airline = d.airline || null;
    body.note = d.quote_description || null;
    body.quote_amount = breakdown.total;
    body.amount = breakdown.total;
    body.cost_amount = breakdown.cost;
    body.extra_patch = {
      pax_cost_rates: { adt: d.cost_adt, chd: d.cost_chd, inf: d.cost_inf },
      pax_profit_rates: { adt: d.profit_adt, chd: d.profit_chd, inf: d.profit_inf },
      quoted_total: breakdown.total,
      quotation_note: d.quote_description || null,
      quote_valid_till: d.quote_valid_till || null,
      quoted_at: d.quoted_at || null,
    };
    return body;
  }

  if (status === 'temporary_booking') {
    const resolvedSystem = resolveBookingSystemValue(d.booking_system, d.booking_system_other);
    let quoted = ticketQuotedTotal(lead) || d.quoted_total || null;
    if (isTicketQuickIntake(lead)) {
      const pax = ticketPax(lead.extra || {}, lead);
      const breakdown = calcTicketQuoteTotals(pax, d);
      quoted = breakdown.total;
      const advance = d.advance ?? 0;
      const balance = quoted != null ? Math.max(0, Math.round(Number(quoted) - advance)) : null;
      body.stage_data = {
        cost_adt: d.cost_adt ?? null,
        profit_adt: d.profit_adt ?? null,
        cost_chd: d.cost_chd ?? null,
        profit_chd: d.profit_chd ?? null,
        cost_inf: d.cost_inf ?? null,
        profit_inf: d.profit_inf ?? null,
        cost_total: breakdown.cost,
        profit_total: breakdown.profit,
        total: breakdown.total,
        pax,
        pnr: d.pnr,
        booking_system: resolvedSystem,
        booking_system_other: d.booking_system === 'Other' ? (d.booking_system_other || null) : null,
        booking_expires: d.booking_expires,
        quoted_total: quoted,
        advance,
        balance_due: balance,
      };
      body.pnr = d.pnr;
      body.quote_amount = breakdown.total;
      body.amount = breakdown.total;
      body.cost_amount = breakdown.cost;
      body.followup_date = datetimeLocalToDate(d.booking_expires);
      body.extra_patch = {
        pax,
        pax_cost_rates: { adt: d.cost_adt, chd: d.cost_chd, inf: d.cost_inf },
        pax_profit_rates: { adt: d.profit_adt, chd: d.profit_chd, inf: d.profit_inf },
        quoted_total: breakdown.total,
        booking_system: resolvedSystem,
        booking_system_other: body.stage_data.booking_system_other,
        booking_expires: d.booking_expires,
        advance_paid: advance,
        balance_due: balance,
      };
      return body;
    }
    const advance = d.advance ?? 0;
    const balance = quoted != null ? Math.max(0, Math.round(Number(quoted) - advance)) : null;
    body.stage_data = {
      pnr: d.pnr,
      booking_system: resolvedSystem,
      booking_system_other: d.booking_system === 'Other' ? (d.booking_system_other || null) : null,
      booking_expires: d.booking_expires,
      quoted_total: quoted,
      advance,
      balance_due: balance,
    };
    body.pnr = d.pnr;
    body.followup_date = datetimeLocalToDate(d.booking_expires);
    body.extra_patch = {
      booking_system: resolvedSystem,
      booking_system_other: body.stage_data.booking_system_other,
      booking_expires: d.booking_expires,
      advance_paid: advance,
      balance_due: balance,
    };
    return body;
  }

  if (status === 'booking_confirmed') {
    const quoted = ticketQuotedTotal(lead) || sd(lead, status).quoted_total || 0;
    const advance = advancePaidFromLead(lead);
    const payment = d.payment_amount || 0;
    const totalPaid = advance + payment;
    const fullyPaid = quoted > 0 && totalPaid >= quoted;
    const balanceDue = quoted > 0 ? Math.max(0, Math.round(quoted - totalPaid)) : null;
    body.stage_data = {
      paid_on: d.paid_on,
      payment_amount: payment,
      quoted_total: quoted || null,
      advance_paid: advance,
      balance_due: balanceDue,
      fully_paid: fullyPaid,
      remain_pay_by: fullyPaid ? null : (d.remain_pay_by || null),
      note: d.note || null,
    };
    body.sold_amount = payment;
    if (d.note) body.note = d.note;
    body.followup_date = fullyPaid ? null : datetimeLocalToDate(d.remain_pay_by);
    return body;
  }

  if (status === 'ticket_issued') {
    const quoted = ticketQuotedTotal(lead) || sd(lead, status).quoted_total || 0;
    const advance = advancePaidFromLead(lead);
    const payment = d.payment_amount ?? Math.max(0, Math.round((quoted || 0) - (advance || 0)));
    const totalPaid = advance + payment;
    body.stage_data = {
      issued_date: d.issued_date,
      pnr: d.pnr,
      payment_amount: payment,
      quoted_total: quoted || null,
      advance_paid: advance,
      fully_paid: quoted > 0 && totalPaid >= quoted,
      note: d.note || null,
    };
    body.pnr = d.pnr;
    body.sold_amount = payment;
    if (d.note) body.note = d.note;
    return body;
  }

  if (type === 'ticket' && status === 'new') {
    return { status, note: d.note || null };
  }

  return body;
}

export function validateStageAnswers(type, status, lead, answers = {}) {
  const body = buildStatusPatchBody(type, status, lead, answers);

  if (type === 'ticket' && status === 'quotation_sent') {
    if (isTicketQuickIntake(lead)) {
      if (!body.stage_data.quoted_at) return 'Enter quotation sent date/time';
      return null;
    }
    const pax = ticketPax(lead.extra || {}, lead);
    if (pax.adt > 0 && body.stage_data.cost_adt == null) return 'Enter net cost per adult';
    if (pax.adt > 0 && body.stage_data.profit_adt == null) return 'Enter profit per adult';
    if (!body.stage_data.airline) return 'Enter airline';
    if (!body.stage_data.quoted_at) return 'Enter quotation sent date/time';
  }
  if (type === 'ticket' && status === 'temporary_booking') {
    const d = body.stage_data;
    if (!d.pnr) return 'Enter PNR';
    if (!d.booking_system) return 'Select where the booking was made';
    if (!d.booking_expires) return 'Enter booking expiry date/time';
    if (isTicketQuickIntake(lead)) {
      if (!body.quote_amount && !body.amount) return 'Enter per-pax cost and profit rates';
    }
  }
  if (type === 'ticket' && status === 'booking_confirmed') {
    const d = body.stage_data;
    if (!d.paid_on) return 'Enter paid date';
    if (!body.sold_amount) return 'Enter payment amount';
    if (!d.fully_paid && (d.quoted_total || 0) > 0 && !d.remain_pay_by) {
      return 'Enter when the remaining balance will be paid';
    }
  }
  if (type === 'ticket' && status === 'ticket_issued') {
    const d = body.stage_data;
    if (!d.issued_date) return 'Enter issued date';
    if (!d.pnr) return 'Enter PNR';
  }

  return null;
}
